import React from 'react';

/**
 * Черновики форм в localStorage: ответ в тикете, задача, новость, сообщение
 * в чате не теряются при уходе со страницы, перезагрузке и закрытии вкладки.
 *
 *   const draft = useDraft(`ticket-reply:${id}`, '');
 *   <Editor value={draft.value} onChange={draft.setValue} />
 *   <DraftHint status={draft.status} onClear={draft.clear} />
 *   // после успешной отправки:
 *   draft.clear();
 *
 * - Запись с задержкой (debounce), а при смене ключа, уходе со страницы
 *   и размонтировании — сразу, чтобы последние символы не пропали.
 * - Черновик старше 7 дней считается протухшим и удаляется.
 * - Пустое значение (как initial, или текст без символов) не хранится.
 * - key = null — черновик выключен (например, форма редактирования ещё
 *   не знает, какую запись редактирует).
 *
 * Никогда не храните так пароли, коды, платёжные данные.
 */

export const DRAFT_PREFIX = 'draft:v1:';
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const DEFAULT_DEBOUNCE_MS = 600;

export type DraftStatus = 'idle' | 'saved' | 'restored';

interface Stored<T> { v: T; t: number }

/** Текст без разметки: «<p><br></p>» из редактора — это пусто, а картинка — нет */
function isBlankText(s: string): boolean {
  if (/<img\b/i.test(s)) return false;
  return s.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() === '';
}

export function isDraftEmpty(value: unknown, initial: unknown): boolean {
  if (value == null) return true;
  if (typeof value === 'string') return isBlankText(value);
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') {
    if (JSON.stringify(value) === JSON.stringify(initial)) return true;
    // Объект формы пуст, если пусты все его текстовые поля
    return Object.values(value as Record<string, unknown>)
      .every(v => typeof v !== 'string' || isBlankText(v));
  }
  return false;
}

export function readDraft<T>(key: string, now = Date.now()): T | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Stored<T>;
    if (!parsed || typeof parsed.t !== 'number' || now - parsed.t > DRAFT_TTL_MS) {
      window.localStorage.removeItem(DRAFT_PREFIX + key);
      return null;
    }
    return parsed.v;
  } catch {
    return null;
  }
}

export function writeDraft<T>(key: string, value: T, empty: boolean, now = Date.now()): void {
  try {
    if (empty) window.localStorage.removeItem(DRAFT_PREFIX + key);
    else window.localStorage.setItem(DRAFT_PREFIX + key, JSON.stringify({ v: value, t: now } as Stored<T>));
  } catch { /* приватный режим или переполнение: черновик живёт только в памяти */ }
}

export function removeDraft(key: string): void {
  try { window.localStorage.removeItem(DRAFT_PREFIX + key); } catch { /* приватный режим */ }
}

/** Удаляет протухшие черновики. Вызывается один раз за загрузку страницы. */
export function pruneDrafts(now = Date.now()): void {
  try {
    const ls = window.localStorage;
    const keys: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (k && k.startsWith(DRAFT_PREFIX)) keys.push(k);
    }
    keys.forEach(k => readDraft(k.slice(DRAFT_PREFIX.length), now));
  } catch { /* приватный режим */ }
}

/** Стереть все черновики — при выходе из аккаунта (компьютер может быть общим) */
export function clearAllDrafts(): void {
  try {
    const ls = window.localStorage;
    const keys: string[] = [];
    for (let i = 0; i < ls.length; i++) {
      const k = ls.key(i);
      if (k && k.startsWith(DRAFT_PREFIX)) keys.push(k);
    }
    keys.forEach(k => ls.removeItem(k));
  } catch { /* приватный режим */ }
}

let pruned = false;

/** Объект из хранилища поверх initial: новые поля формы получают значения по умолчанию */
function merge<T>(initial: T, stored: T): T {
  if (initial && stored && typeof initial === 'object' && typeof stored === 'object' && !Array.isArray(initial)) {
    return { ...initial, ...stored };
  }
  return stored;
}

interface State<T> { key: string | null; value: T; touched: boolean; status: DraftStatus }

export interface DraftOptions<T> {
  debounceMs?: number;
  /** Своё правило «пусто» — по умолчанию пустой текст или значение, равное initial */
  isEmpty?: (value: T) => boolean;
}

export interface Draft<T> {
  value: T;
  setValue: React.Dispatch<React.SetStateAction<T>>;
  /** idle — нечего показывать; saved — сохранили набранное; restored — подставили сохранённое */
  status: DraftStatus;
  /** Стереть черновик и вернуть initial: после успешной отправки и по кнопке «Очистить» */
  clear: () => void;
}

export function useDraft<T>(key: string | null, initial: T, options: DraftOptions<T> = {}): Draft<T> {
  const initialRef = React.useRef(initial);
  initialRef.current = initial;
  const optsRef = React.useRef(options);
  optsRef.current = options;
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;

  const empty = React.useCallback((v: T) => (
    optsRef.current.isEmpty ? optsRef.current.isEmpty(v) : isDraftEmpty(v, initialRef.current)
  ), []);

  const load = React.useCallback((k: string | null): State<T> => {
    if (!pruned) { pruned = true; pruneDrafts(); }
    const stored = k ? readDraft<T>(k) : null;
    if (stored != null && !empty(stored)) {
      return { key: k, value: merge(initialRef.current, stored), touched: false, status: 'restored' };
    }
    return { key: k, value: initialRef.current, touched: false, status: 'idle' };
  }, [empty]);

  const [state, setState] = React.useState<State<T>>(() => load(key));
  let cur = state;
  if (state.key !== key) {
    // Сменился ключ (другой тикет, другой диалог) — подставляем его черновик
    // в том же рендере, без кадра со старым текстом
    cur = load(key);
    setState(cur);
  }

  const pending = React.useRef<{ key: string; value: T } | null>(null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = React.useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const p = pending.current;
    if (!p) return false;
    pending.current = null;
    const isEmpty = empty(p.value);
    writeDraft(p.key, p.value, isEmpty);
    return !isEmpty;
  }, [empty]);

  // Отложенная запись того, что набрал пользователь (подставленное из
  // хранилища заново не пишем)
  React.useEffect(() => {
    if (!cur.touched || !cur.key) return;
    pending.current = { key: cur.key, value: cur.value };
    if (timer.current) clearTimeout(timer.current);
    const forKey = cur.key;
    timer.current = setTimeout(() => {
      const saved = flush();
      setState(s => (s.key === forKey ? { ...s, status: saved ? 'saved' : 'idle' } : s));
    }, debounceMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur.key, cur.value, cur.touched]);

  // Смена ключа и размонтирование — дописываем немедленно
  React.useEffect(() => () => { flush(); }, [cur.key, flush]);

  // Закрытие вкладки / переход на другой сайт
  React.useEffect(() => {
    const onHide = () => { flush(); };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onHide);
    };
  }, [flush]);

  const setValue = React.useCallback<React.Dispatch<React.SetStateAction<T>>>((action) => {
    setState(s => {
      const next = typeof action === 'function' ? (action as (p: T) => T)(s.value) : action;
      if (Object.is(next, s.value)) return s;
      return { ...s, value: next, touched: true };
    });
  }, []);

  // Ключ в ref: clear() после успешной отправки может прийти уже после
  // размонтирования (форма закрылась, страница сменилась) — стереть всё равно надо
  const keyRef = React.useRef(cur.key);
  keyRef.current = cur.key;

  const clear = React.useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    pending.current = null;
    if (keyRef.current) removeDraft(keyRef.current);
    setState(s => ({ key: s.key, value: initialRef.current, touched: false, status: 'idle' }));
  }, []);

  return { value: cur.value, setValue, status: cur.status, clear };
}

/* ═══ Черновик формы-редактора существующих записей ═══════════════════
   Для редакторов, где поля живут в своих useState и заполняются с сервера
   (новость, страница, правило, вопрос FAQ). Хук не владеет значением —
   он сохраняет value, пока форма dirty (отличается от сохранённого), и по
   запросу подставляет черновик поверх только что загруженной записи:

     const fd = useFormDraft(selectedId ? `news:${selectedId}` : 'news:new', snapshot, dirty);
     // после загрузки записи с сервера:
     fd.restore(`news:${id}`, serverSnapshot, applySnapshot);
     // после успешного сохранения:
     fd.clear();
*/

export interface FormDraft<T> {
  status: DraftStatus;
  /**
   * Подставить черновик ключа key, если он есть и отличается от base
   * (того, что пришло с сервера). Возвращает true, если подставили.
   */
  restore: (key: string, base: T, apply: (v: T) => void) => boolean;
  /** Стереть черновик текущего ключа (после сохранения, «Очистить») */
  clear: () => void;
}

export function useFormDraft<T>(key: string | null, value: T, dirty: boolean, debounceMs = DEFAULT_DEBOUNCE_MS): FormDraft<T> {
  const [st, setSt] = React.useState<{ key: string | null; status: DraftStatus }>({ key, status: 'idle' });
  const status: DraftStatus = st.key === key ? st.status : 'idle';

  const json = JSON.stringify(value);
  // Последнее, что записали или подставили: повторно не пишем
  const lastJson = React.useRef<string | null>(null);
  const pending = React.useRef<{ key: string; json: string; value: T } | null>(null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const keyRef = React.useRef(key);
  keyRef.current = key;

  const flush = React.useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    writeDraft(p.key, p.value, false);
    lastJson.current = p.json;
  }, []);

  React.useEffect(() => {
    if (!key || !dirty || json === lastJson.current) return;
    pending.current = { key, json, value };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      flush();
      setSt({ key, status: 'saved' });
    }, debounceMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, json, dirty]);

  React.useEffect(() => () => { flush(); }, [key, flush]);

  React.useEffect(() => {
    const onHide = () => { flush(); };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('beforeunload', onHide);
    };
  }, [flush]);

  const restore = React.useCallback((k: string, base: T, apply: (v: T) => void) => {
    if (!pruned) { pruned = true; pruneDrafts(); }
    const stored = readDraft<T>(k);
    if (stored == null) { setSt({ key: k, status: 'idle' }); return false; }
    const merged = merge(base, stored);
    const mergedJson = JSON.stringify(merged);
    if (mergedJson === JSON.stringify(base)) {
      // Черновик совпал с сохранённым — он больше не нужен
      removeDraft(k);
      setSt({ key: k, status: 'idle' });
      return false;
    }
    lastJson.current = mergedJson;
    apply(merged);
    setSt({ key: k, status: 'restored' });
    return true;
  }, []);

  const clear = React.useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    pending.current = null;
    lastJson.current = null;
    if (keyRef.current) removeDraft(keyRef.current);
    setSt({ key: keyRef.current, status: 'idle' });
  }, []);

  return { status, restore, clear };
}
